import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import type { FunctionReturnType } from "convex/server";
import {
  LuArrowUpRight,
  LuBell,
  LuCircleCheck,
  LuGraduationCap,
  LuListTodo,
  LuMessagesSquare,
  LuTriangleAlert,
  LuUsersRound,
} from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { ListSkeleton } from "../components/LoadingSkeletons";
import { NotificationMessage } from "../components/NotificationMessage";
import { OverdueTab, overdueFrame } from "../components/OverdueTab";
import { PageHeader } from "../components/PageHeader";
import { ProgressBar } from "../components/ProgressRows";
import { useEffectivePersonId } from "../impersonation";
import { isActivityOverdue, viennaToday } from "@/lib/activityDates";
import { formatDay } from "./ActivityAdminShared";
import { formatRelativeTime } from "./Notifications";

/**
 * The dashboard.
 *
 * It used to open on how many people are in the directory and how many groups
 * exist -- true, and no business of the person reading it. A dashboard answers
 * one question: what do I need to do. Everything here is about the viewer's
 * own work, and every panel that has nothing to say is absent rather than
 * empty, so a PhD student and a group head get different pages without either
 * choosing a mode.
 *
 * Built from the queries the other pages already use rather than a dashboard
 * query of its own: every one of them already follows impersonation and
 * already enforces its own permissions, and a second implementation of
 * "what is this person waiting on" is a second thing to get wrong.
 */

type Assignment = FunctionReturnType<typeof api.personActivities.listForViewer>[number];
type Programme = FunctionReturnType<typeof api.programmeProgress.forViewer>;

function relativeDay(day: string | null, today: string): string {
  if (day === null) {
    return "No date";
  }
  if (day === today) {
    return "Today";
  }

  const diff = Math.round(
    (new Date(`${day}T12:00:00Z`).getTime() - new Date(`${today}T12:00:00Z`).getTime())
    / 86_400_000,
  );

  if (diff === 1) {
    return "Tomorrow";
  }
  if (diff === -1) {
    return "Yesterday";
  }
  if (diff < 0) {
    return `${Math.abs(diff)} days ago`;
  }
  if (diff < 30) {
    return `In ${diff} days`;
  }
  return formatDay(day);
}

/** A to-do row's date, said the way it matters: how late, or how soon. */
function dueLabel(day: string | null, today: string): string {
  if (day === null) {
    return "No date";
  }
  const diff = Math.round(
    (new Date(`${day}T12:00:00Z`).getTime() - new Date(`${today}T12:00:00Z`).getTime())
    / 86_400_000,
  );
  if (diff < 0) {
    return `${Math.abs(diff)} ${diff === -1 ? "day" : "days"} late`;
  }
  return `Due ${relativeDay(day, today).replace(/^In /, "in ").replace(/^(Today|Tomorrow)$/, (word) => word.toLowerCase())}`;
}

// The routes a panel's "see everything" link can point at. Spelled out rather
// than typed as string so the router still checks them.
type PanelRoute =
  | "/notifications"
  | "/activities"
  | "/my-programme"
  | "/my-group"
  | "/cemm-chat";

/** A panel, so that the six of them look like one thing. */
function Panel(props: {
  title: string;
  icon: typeof LuBell;
  tone?: string;
  action?: { label: string; to: PanelRoute; search?: { tab: "reviews" } };
  children: React.ReactNode;
}) {
  return (
    // A large icon, softened, in the top-left corner and set in the card's
    // padding rather than in the header row, which keeps its height; the
    // title starts just clear of it. It marks the panel at a glance without
    // outweighing the title.
    <Card.Root borderWidth="1px" borderColor="border" position="relative" overflow="hidden">
      <Icon
        as={props.icon}
        position="absolute"
        top="1rem"
        left="0.5rem"
        boxSize="3rem"
        color={props.tone ?? "blue.500"}
        opacity={0.3}
        strokeWidth={1.5}
        pointerEvents="none"
        aria-hidden
      />
      <Card.Body gap={4} p={6} position="relative">
        <Flex align="center" justify="space-between" gap={4}>
          <Heading size="lg" pl="2.25rem">{props.title}</Heading>
          {props.action === undefined ? null : (
            <Button asChild variant="ghost" size="sm" colorPalette="blue">
              <Link to={props.action.to} search={props.action.search}>
                {props.action.label}
                <LuArrowUpRight />
              </Link>
            </Button>
          )}
        </Flex>
        {props.children}
      </Card.Body>
    </Card.Root>
  );
}

type TodoItem = {
  key: string;
  personActivityId: Id<"personActivities">;
  title: string;
  /** Whose and which activity, or where one's own stands. */
  context: string;
  /** What it asks, where it is somebody else's work; null on one's own. */
  kind: "Sign off" | "Fill in" | null;
  due: string | null;
  overdue: boolean;
};

function EmptyLine({ children }: { children: React.ReactNode }) {
  return (
    <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={4} py={4}>
      <Text fontSize="sm" color="fg.muted">{children}</Text>
    </Box>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const effectivePersonId = useEffectivePersonId();
  const personArg = { effectivePersonId: effectivePersonId as Id<"persons"> | null };

  const viewerProfile = useQuery(api.persons.viewerProfile, personArg);
  const notifications = useQuery(api.notifications.get, personArg);
  const assignments = useQuery(api.personActivities.listForViewer, personArg) as
    | Assignment[]
    | undefined;
  const programme = useQuery(api.programmeProgress.forViewer, {
    ...personArg,
    personId: null,
  }) as Programme | undefined;
  const queue = useQuery(api.reviews.supervisorQueue, personArg);
  const usage = useQuery(api.chatUsage.myUsageCells, {
    ...personArg,
    // The calendar month, as the CeMM Chat page counts it: a rolling thirty
    // days matched nothing anybody is billed or asked about.
    fromDay: `${viennaToday().slice(0, 8)}01`,
    toDay: viennaToday(),
    bucket: "day" as const,
  });

  const today = viennaToday();

  // One to-do, computed from what is waiting now: the steps to sign off and
  // to fill in (the review queue), and the viewer's own activities with a
  // deadline. Notifications are news, not the to-do. They are written once,
  // when something happens; this list is worked out afresh, so an item leaves
  // it the moment it is done -- by the viewer or by anybody else -- and a
  // prompt that no longer asks anything is marked so where it is read
  // (convex/notificationState.ts).
  const pendingReviews = queue?.pendingReviews ?? [];

  // Required work only. Encouraged activities are real work that never counts
  // as late (model §7), and a dashboard that chased somebody about an optional
  // patent application would be worse than one that said nothing. They are
  // counted separately below instead.
  const live = useMemo(
    () => (assignments ?? []).filter((row) =>
      row.status !== "completed"
      && row.status !== "archived"
      && row.status !== "cancelled"),
    [assignments],
  );
  const encouragedCount = useMemo(
    () => live.filter((row) => !row.required).length,
    [live],
  );

  const todo = useMemo<TodoItem[]>(() => {
    const reviews: TodoItem[] = pendingReviews.map((review) => {
      const due = review.taskDueDay ?? review.dueDay;
      return {
        key: `task:${review.taskId}`,
        personActivityId: review.personActivityId,
        title: review.taskTitle,
        context: `${review.personName} · ${review.activityTitle}`,
        // Two different jobs: signing off work somebody else submitted,
        // versus filling in a step that is yours to do.
        kind: review.kind === "approve" ? "Sign off" : "Fill in",
        due,
        overdue: due !== null && due < today,
      };
    });
    // The viewer's own, where it is their move: not while it waits on a
    // reviewer, which is somebody else's to-do.
    const own: TodoItem[] = live
      .filter((row) => row.required && row.dueDay !== null && row.status !== "awaiting_review")
      .map((row) => ({
        key: `activity:${row._id}`,
        personActivityId: row._id,
        title: row.titleSnapshot,
        context: row.statusLabel,
        kind: null,
        due: row.dueDay,
        overdue: isActivityOverdue(row, today),
      }));
    return [...reviews, ...own].sort((left, right) =>
      (left.due ?? "9999-12-31").localeCompare(right.due ?? "9999-12-31"));
  }, [pendingReviews, live, today]);
  const overdueCount = todo.filter((item) => item.overdue).length;

  // News: whatever is unread. A prompt that stopped asking already reads as
  // read, so nothing here asks for what the to-do does not.
  const news = useMemo(
    () => (notifications ?? []).filter((item) => !item.isRead),
    [notifications],
  );

  // Heads only. A group panel for somebody who heads nothing is a panel about
  // somebody else's group.
  const headedGroups = useMemo(
    () => (viewerProfile?.affiliations ?? []).filter((affiliation) =>
      affiliation.isCurrent
      && affiliation.headRole !== null
      && affiliation.orgUnit !== null),
    [viewerProfile],
  );

  // Day cells, so today is a slice of the month rather than a second query.
  const usageTotals = useMemo(() => {
    const cost = (cells: NonNullable<typeof usage>) => cells.reduce((sum, cell) => sum + cell.costUsd, 0);
    const cells = usage ?? [];
    return {
      month: cost(cells),
      today: cost(cells.filter((cell) => cell.bucket === today)),
    };
  }, [usage, today]);

  const greetingName =
    viewerProfile?.person?.firstName ?? viewerProfile?.auth?.name?.split(" ")[0] ?? null;

  const currentProgrammeYear = programme?.years.find((year) => year.isCurrent) ?? null;
  const TODO_SHOWN = 8;

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={greetingName ? `Welcome back, ${greetingName}` : "Welcome to CeMMbase"}
        description="What is waiting for you, and what is new."
        breadcrumbs={[{ label: "CeMMbase" }]}
        loading={viewerProfile === undefined}
      />

      {/* Three columns on a wide screen: the to-do and the news, the two
          lists that grow, one above the other across the first two; the
          small panels stacked in the third. On a very wide one the to-do
          and the news stand side by side, a column each. */}
      <Grid
        gap={6}
        px={{ base: 6, sm: 8, lg: 10 }}
        py={{ base: 6, lg: 8 }}
        templateColumns={{ base: "1fr", xl: "repeat(3, 1fr)" }}
        alignItems="start"
      >
        <Grid
          gap={6}
          gridColumn={{ xl: "span 2" }}
          templateColumns={{ base: "1fr", "2xl": "repeat(2, 1fr)" }}
          alignItems="start"
          minW={0}
        >
          <Panel
            title="Your to-do"
            icon={LuListTodo}
            tone={overdueCount > 0 ? "red.500" : "blue.500"}
            action={pendingReviews.length > 0
              ? { label: "My reviews", to: "/activities", search: { tab: "reviews" } }
              : { label: "All activities", to: "/activities" }}
          >
            {queue === undefined || assignments === undefined ? (
              <ListSkeleton label="Loading" rows={4} />
            ) : todo.length === 0 ? (
              <HStack gap={3} borderWidth="1px" borderStyle="dashed" borderColor="border" px={4} py={4}>
                <Icon as={LuCircleCheck} boxSize={5} color="green.500" />
                <Text fontSize="sm" color="fg.muted">
                  Nothing is waiting for you. Anything new will appear here.
                </Text>
              </HStack>
            ) : (
              <Stack gap={3}>
                {overdueCount > 0 ? (
                  <HStack gap={2} color="red.600">
                    <Icon as={LuTriangleAlert} boxSize={4} />
                    <Text fontSize="sm" fontWeight="semibold">{overdueCount} overdue</Text>
                  </HStack>
                ) : null}
                {todo.slice(0, TODO_SHOWN).map((item) => (
                  <Flex
                    key={item.key}
                    position="relative"
                    {...overdueFrame(item.overdue)}
                    align="center"
                    justify="space-between"
                    gap={4}
                    bg="bg"
                    px={4}
                    pb={3}
                    // Room for the overdue tab on every row, so a late one
                    // does not sit lower than the rest.
                    pt={5}
                    cursor="pointer"
                    onClick={() => {
                      void navigate({
                        to: "/activities/$personActivityId",
                        params: { personActivityId: item.personActivityId },
                        search: { from: "dashboard" },
                      });
                    }}
                  >
                    {item.overdue ? <OverdueTab /> : null}
                    <Box minW={0}>
                      <Text fontWeight="semibold" fontSize="sm">{item.title}</Text>
                      <Text mt={1} fontSize="sm" color="fg.muted">{item.context}</Text>
                    </Box>
                    {/* What it asks, over when: one narrow column, so the
                        title and whose it is keep the width. */}
                    <Stack gap={1.5} align="flex-end" flexShrink={0}>
                      {item.kind === null ? null : (
                        <Badge colorPalette={item.kind === "Sign off" ? "teal" : "purple"} variant="subtle">
                          {item.kind}
                        </Badge>
                      )}
                      <Text fontSize="xs" color={item.overdue ? "red.600" : "fg.muted"} whiteSpace="nowrap">
                        {dueLabel(item.due, today)}
                      </Text>
                    </Stack>
                  </Flex>
                ))}
                {todo.length > TODO_SHOWN ? (
                  <Text fontSize="xs" color="fg.muted">
                    …and {todo.length - TODO_SHOWN} more, in the review queue and your activities.
                  </Text>
                ) : null}
                {encouragedCount > 0 ? (
                  <Text fontSize="xs" color="fg.muted">
                    Plus {encouragedCount} encouraged {encouragedCount === 1 ? "activity" : "activities"},
                    which never fall due.
                  </Text>
                ) : null}
              </Stack>
            )}
          </Panel>

          <Panel
            title="News"
            icon={LuBell}
            tone="orange.500"
            action={{ label: "All notifications", to: "/notifications" }}
          >
            {notifications === undefined ? (
              <ListSkeleton label="Loading" rows={3} />
            ) : news.length === 0 ? (
              <EmptyLine>Nothing new.</EmptyLine>
            ) : (
              <Stack gap={2}>
                {news.slice(0, 6).map((item) => (
                  <Box
                    key={item._id}
                    borderWidth="1px"
                    borderColor="border"
                    bg="bg"
                    p={4}
                    cursor={item.actionUrl ? "pointer" : undefined}
                    _hover={item.actionUrl ? { borderColor: "blue.400" } : undefined}
                    onClick={() => {
                      if (item.actionUrl) {
                        void navigate({ to: item.actionUrl });
                      }
                    }}
                  >
                    {/* When, top right, as on the Notifications page. */}
                    <Flex justify="space-between" align="baseline" gap={3}>
                      <Text fontWeight="semibold" fontSize="sm">{item.title}</Text>
                      <Text fontSize="xs" color="fg.muted" flexShrink={0} whiteSpace="nowrap">
                        {formatRelativeTime(item.createdAt)}
                      </Text>
                    </Flex>
                    <Text mt={1} fontSize="sm" color="fg.muted"><NotificationMessage message={item.message} /></Text>
                  </Box>
                ))}
                {news.length > 6 ? (
                  <Text fontSize="xs" color="fg.muted">…and {news.length - 6} more.</Text>
                ) : null}
              </Stack>
            )}
          </Panel>
        </Grid>

        <Stack gap={6} minW={0}>
          {programme === undefined || programme === null ? null : (
            <Panel
              title="Your programme"
              icon={LuGraduationCap}
              tone="purple.500"
              action={{ label: "Full record", to: "/my-programme" }}
            >
              <SimpleGrid columns={2} gap={4}>
                <Box>
                  <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.16em">
                    Year
                  </Text>
                  <Text fontSize="2xl" fontWeight="semibold">{programme.currentYear}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.16em">
                    Cohort
                  </Text>
                  <Text fontSize="2xl" fontWeight="semibold">
                    {programme.cohort ?? "—"}
                  </Text>
                </Box>
              </SimpleGrid>
              {currentProgrammeYear === null ? null : (
                <Box>
                  <Text mb={2} fontSize="xs" color="fg.muted">
                    Year {currentProgrammeYear.year}
                  </Text>
                  <ProgressBar rows={currentProgrammeYear.rows} />
                </Box>
              )}
            </Panel>
          )}

          {headedGroups.length === 0 ? null : (
            <Panel
              title={headedGroups.length === 1 ? "Your group" : "Your groups"}
              icon={LuUsersRound}
              tone="cyan.500"
              action={{ label: "Open my group", to: "/my-group" }}
            >
              <Stack gap={3}>
                {headedGroups.map((affiliation) => (
                  <Text key={affiliation.orgUnit!._id} fontWeight="semibold" fontSize="sm">
                    {affiliation.orgUnit!.name}
                    <Text as="span" ml={2} fontSize="xs" color="fg.muted" fontWeight="normal">
                      {affiliation.roleLabel}
                    </Text>
                  </Text>
                ))}
                <SimpleGrid columns={3} gap={3}>
                  <Box>
                    <Text fontSize="xl" fontWeight="semibold">{queue?.supervisedPeople ?? 0}</Text>
                    <Text fontSize="xs" color="fg.muted">people supervised</Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xl"
                      fontWeight="semibold"
                      color={(queue?.overdueActivities ?? 0) > 0 ? "red.600" : undefined}
                    >
                      {queue?.overdueActivities ?? 0}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">activities overdue</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xl" fontWeight="semibold">{queue?.blockedActivities ?? 0}</Text>
                    <Text fontSize="xs" color="fg.muted">activities blocked</Text>
                  </Box>
                </SimpleGrid>
              </Stack>
            </Panel>
          )}

          {/* Shown at zero too: an empty month is an answer, and a panel
              that comes and goes moves everything below it. */}
          <Panel
            title="CeMM-Chat"
            icon={LuMessagesSquare}
            tone="pink.500"
            action={{ label: "Details", to: "/cemm-chat" }}
          >
            {/* Cost only: requests and tokens are on the CeMM Chat page,
                and what it costs is the figure people check. Today first,
                as the more immediate of the two. */}
            <SimpleGrid columns={2} gap={3}>
              {([
                ["today", usageTotals.today],
                ["this month", usageTotals.month],
              ] as const).map(([label, costUsd]) => (
                <Box key={label}>
                  {usage === undefined
                    ? <Skeleton height="7" width="16" mb={1} />
                    : <Text fontSize="xl" fontWeight="semibold">${costUsd.toFixed(2)}</Text>}
                  <Text fontSize="xs" color="fg.muted">{label}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Panel>
        </Stack>
      </Grid>
    </Box>
  );
}
